<script setup>
import { ref, computed } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import capitalize from 'lodash/capitalize'

const props = defineProps({
  modelValue: {
    type: [String, Object, Array, null],
    default: null
  },
  options: {
    type: Array,
    required: true
  },
  placeholder: {
    type: String,
    required: true
  },
  displayValue: {
    type: Function,
    default: (value) => value ? (value) : null
  },
  optionValue: {
    type: Function,
    default: (option) => option
  },
  optionDisplay: {
    type: Function,
    default: (option) => (option)
  },
  searchable: {
    type: Boolean,
    default: false
  },
  displayPrefix: {
    type: String,
    default: null
  },
  resetOption: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:modelValue'])
const searchQuery = ref('')

const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options
  
  return props.options.filter(option => 
    props.optionDisplay(option)
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase())
  )
})

const isOptionSelected = (option) => {
  if (Array.isArray(props.modelValue)) {
    return props.modelValue.includes(props.optionValue(option))
  }
  return props.modelValue === props.optionValue(option)
}

const getDisplayValue = computed(() => {
  if (!props.modelValue) return props.placeholder
  let displayValue = props.displayPrefix ? props.displayPrefix : ''
  if (Array.isArray(props.modelValue)) {
    displayValue += props.modelValue?.length > 0
      ? props.modelValue.map(v => props.displayValue(v)).join(', ')
      : props.placeholder
  } else {
    displayValue += props.displayValue(props.modelValue)
  }
  return displayValue
})

const clearSearch = () => {
  searchQuery.value = ''
}
</script>

<template>
  <Listbox
    v-slot="{ open }"
    class="base-listbox"
    :modelValue="modelValue"
    @update:modelValue="(value) => emit('update:modelValue', value)"
    as="div"
  >
    <div class="listbox-overlay" v-if="open"></div>
    <div class="relative">
      <!-- Button -->
      <ListboxButton
        class="listbox-button"
        :class="{
          'filter-selected': modelValue,
        }"
      >
      
        <span class="block truncate">
          {{ getDisplayValue }}
        </span>

        
        <span class="selector-icon">
          <Icon
            icon="heroicons-outline:selector"
            class="h-5 w-5 text-secondary-text"
          />
        </span>
      </ListboxButton>

      <!-- Dropdown -->
      <transition name="fade-fast">
        <ListboxOptions class="listbox-options" static v-show="open">
          <!-- Search Input -->
          <div v-if="searchable" class="px-3 py-2">
            <div class="relative">
              <input
                type="text"
                v-model="searchQuery"
                class="w-full px-3 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-accent-1"
                :placeholder="'Search'"
                @click.stop
              >
              <button
                v-if="searchQuery"
                @click="clearSearch"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <Icon icon="heroicons-outline:x" class="h-4 w-4" />
              </button>
            </div>
          </div>

          <!-- Default Option -->
          <ListboxOption v-if="resetOption" v-slot="{ active }" :value="null" as="template">
            <li
              class="listbox-option small-regular"
              :class="[
                { 'bg-gray-50': active },
                { 'font-bold text-accent-1': !modelValue },
              ]"
            >
              {{ resetOption }}
            </li>
          </ListboxOption>

          <!-- Options List -->
          <ListboxOption
            v-for="(option, index) in filteredOptions"
            :key="index"
            :value="optionValue(option)"
            v-slot="{ active }"
            as="template"
          >
            <li
              class="listbox-option small-regular"
              :class="[
                { 'bg-gray-50': active },
                { 'font-bold text-accent-1': isOptionSelected(option) },
              ]"
            >
              {{ optionDisplay(option) }}
            </li>
          </ListboxOption>

          <!-- No Results Message -->
          <li
            v-if="searchable && searchQuery && !filteredOptions.length"
            class="py-2 px-3 text-sm text-gray-500 text-center"
          >
            No results found
          </li>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>

<style scoped>
.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.1s ease;
}

.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}
</style>